import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  AccordionDetails,
  Stack,
  Accordion,
  AccordionSummary,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const TestFailedAlertPage = ({
  visible,
  title,
  message,
  onClose,
  failedQuestionNumber = [],
  failedQuestionText = [],
  correctAnswer = [],
  failedAnswer = [],
  solutions = [],
  questions,
}) => {
  if (!visible) return null;


  const renderMath = (text) => {
    if (!text) return null;

    // ✅ Ensure text is a string
    if (Array.isArray(text)) {
      text = text.join(", "); // convert array → string
    } else if (typeof text !== "string") {
      text = String(text); // fallback (number, null, etc.)
    }

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return <InlineMath key={index} math={part.slice(2, -2)} />;
      }

      if (part.startsWith("$") && part.endsWith("$")) {
        return <InlineMath key={index} math={part.slice(1, -1)} />;
      }

      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  return (
    <Dialog
      open={visible}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow:
            "0 32px 80px rgba(26,10,60,0.18), 0 2px 8px rgba(0,0,0,0.06)",
          maxHeight: "88vh",
          animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
          "@keyframes popIn": {
            from: { opacity: 0, transform: "scale(0.88) translateY(16px)" },
            to: { opacity: 1, transform: "scale(1) translateY(0)" },
          },
        },
      }}
    >
      {/* Animated top stripe */}
      <Box
        sx={{
          height: 4,
          background: "linear-gradient(90deg,#f97316,#fb923c,#fbbf24,#f97316)",
          backgroundSize: "200% 100%",
          animation: "stripeSlide 3s linear infinite",
          "@keyframes stripeSlide": { to: { backgroundPosition: "-200% 0" } },
        }}
      />

      {/* HEADER */}
      <Box
        sx={{
          px: 4,
          pt: 4,
          pb: 0,
          textAlign: "center",
          background: "linear-gradient(180deg,#fff7f0 0%,#fff 100%)",
          borderBottom: "1px solid rgba(249,115,22,0.1)",
        }}
      >
        {/* Icon ring */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            mx: "auto",
            mb: 2,
            background: "linear-gradient(135deg,#fff1e8,#ffe4cc)",
            border: "2px solid rgba(249,115,22,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "2rem",
            boxShadow: "0 8px 24px rgba(249,115,22,0.15)",
            animation: "pulseRing 2.5s ease-in-out infinite",
            "@keyframes pulseRing": {
              "0%,100%": { boxShadow: "0 8px 24px rgba(249,115,22,0.15)" },
              "50%": { boxShadow: "0 8px 36px rgba(249,115,22,0.3)" },
            },
          }}
        >
          😓
        </Box>

        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            fontSize: "1.6rem",
            fontWeight: 700,
            color: "#1a0a14",
            letterSpacing: "-0.02em",
            mb: 0.75,
          }}
        >
          {title || "Test Failed"}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.88rem",
            color: "rgba(26,10,20,0.5)",
            lineHeight: 1.6,
            maxWidth: 380,
            mx: "auto",
          }}
        >
          {message}
        </Typography>

        {/* Stats strip */}
        <Box
          sx={{
            display: "flex",
            gap: "1px",
            bgcolor: "rgba(249,115,22,0.1)",
            borderTop: "1px solid rgba(249,115,22,0.1)",
            mx: -4,
            mt: 3,
          }}
        >
          {[
            { num: failedQuestionNumber.length, label: "Wrong" },
            {
              num: `${Math.round(
                ((questions?.length - failedQuestionNumber.length) /
                  questions?.length) *
                  100
              )}%`,
              label: "Score",
            },
            { num: "60%", label: "Pass Mark" },
          ].map(({ num, label }) => (
            <Box
              key={label}
              sx={{ flex: 1, py: 1.5, textAlign: "center", bgcolor: "#fff7f0" }}
            >
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  color: "#f97316",
                  display: "block",
                }}
              >
                {num}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "rgba(26,10,20,0.4)",
                  mt: 0.25,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* SECTION LABEL */}
      <Box
        sx={{
          px: 3.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(26,10,20,0.06)",
        }}
      >
        <Box
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#f97316" }}
        />
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.68rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#f97316",
            fontWeight: 500,
          }}
        >
          Review Mistakes
        </Typography>
        <Box
          sx={{
            ml: "auto",
            bgcolor: "rgba(249,115,22,0.1)",
            color: "#f97316",
            px: 1.25,
            py: 0.3,
            borderRadius: 99,
            fontSize: "0.7rem",
            fontFamily: "'JetBrains Mono',monospace",
            fontWeight: 500,
          }}
        >
          {failedQuestionNumber.length} questions
        </Box>
      </Box>

      {/* SCROLLABLE BODY */}
      <DialogContent
        sx={{
          p: "16px 24px",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(249,115,22,0.2)",
            borderRadius: 4,
          },
        }}
      >
        <Stack spacing={1.5}>
          {failedQuestionNumber.map((num, index) => (
            <Accordion
              key={index}
              defaultExpanded={index === 0}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: "#fafafa",
                border: "1px solid rgba(26,10,20,0.07)",
                borderRadius: "14px !important",
                overflow: "hidden",
                "&:before": { display: "none" },
                "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06)" },
                transition: "box-shadow 0.2s",
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMore
                    sx={{ color: "rgba(26,10,20,0.3)", fontSize: 18 }}
                  />
                }
                sx={{
                  bgcolor: "#fff",
                  borderBottom: "1px solid rgba(26,10,20,0.06)",
                  minHeight: 48,
                  px: 2,
                  "& .MuiAccordionSummary-content": {
                    gap: 1.25,
                    alignItems: "center",
                    my: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "rgba(249,115,22,0.1)",
                    color: "#f97316",
                    px: 1.25,
                    py: 0.4,
                    borderRadius: "6px",
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.67rem",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  Q {String(num).padStart(2, "0")}
                </Box>
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.82rem",
                    color: "rgba(26,10,20,0.5)",
                    flex: 1,
                  }}
                >
                  {renderMath(failedQuestionText[index])}
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}
              >
                {/* Wrong answer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    p: 1.25,
                    bgcolor: "rgba(239,68,68,0.05)",
                    border: "1px solid rgba(239,68,68,0.15)",
                    borderRadius: "9px",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", mt: 0.2 }}>
                    ✗
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: "0.63rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#dc2626",
                      fontWeight: 600,
                      mt: 0.3,
                      flexShrink: 0,
                    }}
                  >
                    Your answer
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "rgba(26,10,20,0.7)",
                      lineHeight: 1.55,
                    }}
                  >
                    {renderMath(failedAnswer[index])}
                  </Typography>
                </Box>

                {/* Correct answer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    p: 1.25,
                    bgcolor: "rgba(34,197,94,0.05)",
                    border: "1px solid rgba(34,197,94,0.15)",
                    borderRadius: "9px",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", mt: 0.2 }}>
                    ✓
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: "0.63rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#16a34a",
                      fontWeight: 600,
                      mt: 0.3,
                      flexShrink: 0,
                    }}
                  >
                    Correct
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "rgba(26,10,20,0.7)",
                      lineHeight: 1.55,
                    }}
                  >
                    {renderMath(correctAnswer[index])}
                  </Typography>
                </Box>

                {/* Solution */}
                {solutions[index] && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      p: 1.25,
                      bgcolor: "rgba(99,60,180,0.04)",
                      border: "1px solid rgba(99,60,180,0.12)",
                      borderRadius: "9px",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.8rem", mt: 0.2 }}>
                      💡
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.63rem",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#633cb4",
                        fontWeight: 600,
                        mt: 0.3,
                        flexShrink: 0,
                      }}
                    >
                      Solution
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: "rgba(26,10,20,0.65)",
                        lineHeight: 1.6,
                      }}
                    >
                      {renderMath(solutions[index])}
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 3.5,
          py: 2.5,
          gap: 1.25,
          borderTop: "1px solid rgba(26,10,20,0.07)",
          bgcolor: "#fff",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            flex: 2,
            py: 1.5,
            borderRadius: "11px",
            background: "linear-gradient(135deg,#f97316,#fb923c)",
            color: "#fff",
            fontFamily: "'Sora',sans-serif",
            fontSize: "0.88rem",
            fontWeight: 700,
            boxShadow: "0 4px 18px rgba(249,115,22,0.35)",
            "&:hover": {
              boxShadow: "0 6px 28px rgba(249,115,22,0.5)",
              transform: "translateY(-2px)",
              background: "linear-gradient(135deg,#f97316,#fb923c)",
            },
            transition: "all 0.2s",
          }}
        >
          ↺ &nbsp; Retry Test
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestFailedAlertPage;
